import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setSentryUser } from '../../utils/sentry';
import { trackBusinessMetrics } from '../../utils/sentryMetrics';
import { handleError } from '../../utils/errorNotification';

interface AuthInfo {
  name: string;
  phone: string;
}

/**
 * 🎯 시세조회에서 선택된 기기 정보 타입
 * - sessionStorage에 저장되는 데이터 구조
 */
interface PriceQuoteDevice {
  deviceType: string;
  deviceModel: string;
  deviceImage?: string;
  priceRange?: { min: number; max: number };
  timestamp: number;
}

interface UseAuthenticationProps {
  setForm: (updater: (prev: any) => any) => void;
  setAgreed: (agreed: boolean) => void;
  setIsAuthenticated: (auth: boolean) => void;
  setBlocking: (blocking: boolean) => void;
  scrollToForm: () => void;
}

export const useAuthentication = ({
  setForm,
  setAgreed,
  setIsAuthenticated,
  setBlocking,
  scrollToForm,
}: UseAuthenticationProps) => {
  const location = useLocation();

  // 본인인증 정보 조회
  const fetchCertificationInfo = async (imp_uid: string) => {
    try {
      setBlocking(true);
      const res = await fetch('https://us-central1-refurb-buy.cloudfunctions.net/getCertificationInfo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imp_uid }),
      });
      
      if (!res.ok) {
        throw new Error(`인증 정보 조회 HTTP 에러: ${res.status}`);
      }
      
      const data = await res.json();
      const { name, phone } = data;
      
      if (!name) {
        throw new Error('인증 정보에서 이름을 찾을 수 없습니다');
      }
      
      // Sentry에 사용자 정보 설정
      setSentryUser({ name, phone });
      
      // 인증 성공 메트릭 추적
      trackBusinessMetrics.authenticationSuccess('portone');
      
      setForm(prev => ({ ...prev, name, phone }));
      setAgreed(true);
      setIsAuthenticated(true);
      scrollToForm();
      
    } catch (error) {
      console.error('❌ 본인인증 정보 조회 실패', error);
      
      // 에러 처리 (critical 레벨로 설정)
      handleError(error as Error, {
        business_type: 'auth_failure',
        step: 'certification_info_fetch',
        imp_uid,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent
      }, 'critical');
      
      // 사용자에게 친화적인 에러 메시지
      alert('본인인증 정보를 불러오는데 실패했습니다. 다시 시도해 주세요.');
      
    } finally {
      setBlocking(false);
    }
  };

  /**
   * 🎯 시세조회에서 선택된 기기 자동 추가 처리
   * - sessionStorage에서 priceQuoteDevice 데이터 확인
   * - 인증 성공 후 기기 자동 추가 및 데이터 삭제
   */
  const processPriceQuoteDevice = () => {
    const storedDevice = sessionStorage.getItem('priceQuoteDevice');
    if (storedDevice) {
      try {
        const device: PriceQuoteDevice = JSON.parse(storedDevice);
        
        // 데이터 유효성 검사 (5분 이내 데이터만 유효)
        const isValid = device.timestamp && (Date.now() - device.timestamp) < 5 * 60 * 1000;
        
        if (isValid && device.deviceType && device.deviceModel) {
          console.log('🎯 시세조회 기기 자동 추가:', device);
          
          // 기기 추가 (fromPriceQuote 플래그 추가)
          setForm((prev: any) => ({
            ...prev,
            devices: [
              ...prev.devices,
              {
                deviceType: device.deviceType,
                deviceModel: device.deviceModel,
                fromPriceQuote: true, // 🎯 시세조회에서 자동 추가된 기기 표시
                priceRange: device.priceRange,
                deviceImage: device.deviceImage,
              }
            ]
          }));
        }
      } catch (error) {
        console.error('❌ 시세조회 기기 데이터 파싱 실패:', error);
      } finally {
        // 처리 후 즉시 삭제 (보안)
        sessionStorage.removeItem('priceQuoteDevice');
      }
    }
  };

  // URL 파라미터 및 세션 스토리지 확인
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const imp_uid = searchParams.get('imp_uid');
    const success = searchParams.get('success') === 'true';
    
    if (success && imp_uid) {
      fetchCertificationInfo(imp_uid);
    } else {
      const stored = sessionStorage.getItem('authInfo');
      if (stored) {
        const { name, phone }: AuthInfo = JSON.parse(stored);
        setForm(prev => ({ ...prev, name, phone }));
        setAgreed(true);
        setIsAuthenticated(true);
        scrollToForm();
        
        // 🎯 시세조회 기기 자동 추가 처리
        processPriceQuoteDevice();
      }
    }
  }, [location.search]);

  // 🎯 인증 성공 시 시세조회 기기 처리
  useEffect(() => {
    // setIsAuthenticated가 true로 설정된 직후 실행
    const checkPriceQuoteDevice = () => {
      const storedDevice = sessionStorage.getItem('priceQuoteDevice');
      if (storedDevice) {
        processPriceQuoteDevice();
      }
    };
    
    // 약간의 딜레이 후 확인 (인증 완료 후)
    const timer = setTimeout(checkPriceQuoteDevice, 500);
    return () => clearTimeout(timer);
  }, []);

  return {
    fetchCertificationInfo,
    processPriceQuoteDevice, // 외부에서도 호출 가능하도록 노출
  };
}; 